// ─────────────────────────────────────────────────────────────────────────────
// AliasService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for product alias CRUD and bulk operations.
// Endpoints: /ai/kb/aliases for all alias operations.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { IPageCount } from 'src/app/shared/models/page.model';
import { environment } from 'src/environments/environment';
import { AliasFilters, ICreateAliasPayload, IProductAlias, IUpdateAliasPayload } from '../models/alias.models';
import { IImportPreviewResponse } from '../models/import.models';

@Injectable({
  providedIn: 'root',
})
export class AliasService {
  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public listAliases(
    page: IPageCount,
    key?: string,
    filters?: AliasFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<APIResponse<IProductAlias>> {
    let params = new HttpParams().set('page', page.number).set('limit', page.size);

    if (key) {
      params = params.set('key', key);
    }
    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }
    if (sortMode) {
      params = params.set('sort_mode', sortMode);
    }
    if (filters) {
      if (filters.alias_type) {
        params = params.set('alias_type', filters.alias_type);
      }
      if (filters.source) {
        params = params.set('source', filters.source);
      }
      if (filters.customer_id != null) {
        params = params.set('customer_id', filters.customer_id);
      }
      if (filters.product_id != null) {
        params = params.set('product_id', filters.product_id);
      }
      if (filters.create_date_from) {
        params = params.set('create_date_from', filters.create_date_from);
      }
      if (filters.create_date_to) {
        params = params.set('create_date_to', filters.create_date_to);
      }
    }

    return this.http.get<APIResponse<IProductAlias>>(`${environment.BASE_URL_API}ai/kb/aliases`, { params });
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  public createAlias(payload: ICreateAliasPayload): Observable<APIResponse<IProductAlias>> {
    return this.http.post<APIResponse<IProductAlias>>(`${environment.BASE_URL_API}ai/kb/aliases`, payload);
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  public updateAlias(id: number, payload: IUpdateAliasPayload): Observable<APIResponse<IProductAlias>> {
    return this.http.post<APIResponse<IProductAlias>>(`${environment.BASE_URL_API}ai/kb/aliases/${id}`, payload);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  public deleteAlias(id: number): Observable<APIResponse<IProductAlias>> {
    return this.http.delete<APIResponse<IProductAlias>>(`${environment.BASE_URL_API}ai/kb/aliases/${id}`);
  }

  // ── Bulk delete ────────────────────────────────────────────────────────────

  public bulkDeleteAliases(ids: number[]): Observable<APIResponse<IProductAlias>> {
    return this.http.post<APIResponse<IProductAlias>>(`${environment.BASE_URL_API}ai/kb/aliases/bulk/delete`, { ids });
  }

  // ── Import ─────────────────────────────────────────────────────────────────

  /**
   * Upload a CSV/XLSX file for alias import.
   * @param file FormData with field name 'ufile'
   * @param dryRun If true, validates without persisting (preview mode)
   */
  public importAliases(file: FormData, dryRun = false): Observable<{ object: IImportPreviewResponse }> {
    const url = dryRun ? `${environment.BASE_URL_API}ai/kb/aliases/import?dry_run=1` : `${environment.BASE_URL_API}ai/kb/aliases/import`;
    return this.http.post<{ object: IImportPreviewResponse }>(url, file);
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  public exportAliases(
    format: 'csv' | 'xlsx' = 'xlsx',
    key?: string,
    filters?: AliasFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<Blob> {
    let params = new HttpParams().set('format', format);

    if (key) params = params.set('key', key);
    if (sortBy) params = params.set('sort_by', sortBy);
    if (sortMode) params = params.set('sort_mode', sortMode);
    if (filters) {
      if (filters.alias_type) params = params.set('alias_type', filters.alias_type);
      if (filters.source) params = params.set('source', filters.source);
      if (filters.customer_id != null) params = params.set('customer_id', filters.customer_id);
      if (filters.product_id != null) params = params.set('product_id', filters.product_id);
      if (filters.create_date_from) params = params.set('create_date_from', filters.create_date_from);
      if (filters.create_date_to) params = params.set('create_date_to', filters.create_date_to);
    }

    return this.http.get(`${environment.BASE_URL_API}ai/kb/aliases/export`, {
      params,
      responseType: 'blob',
    });
  }
}
